<template>
  <div>
    <Teleport to="#top-menu-bar" defer>
      <PageTopBar>
        <template #item-name>
          <span>
            Import Workflow from
            {{ isGalaxyEndpoint ? selectedRepo.label : 'RO-Crate' }}
          </span>
        </template>
      </PageTopBar>
    </Teleport>
    <div class="p-3">
      <!-- Info banner for workflow version import -->
      <div v-if="workflowId" class="notification is-info">
        <span class="icon">
          <i class="fa-solid fa-info-circle" />
        </span>
        <span class="text">
          You are importing an additional version for workflow
          <a
            class="is-italic has-text-primary"
            :href="`/${workflowId}`"
            target="_blank"
            rel="noopener"
          >
            {{ workflowName || workflowId }}
          </a>
        </span>
      </div>
      <div class="field mb-5 repository-platform-field">
        <label class="label is-size-7 m-1">Repository platform</label>
        <div class="control">
          <div
            v-if="!isLoadingRepositories && wfRepositories.length === 0"
            class="notification is-warning is-light has-text-weight-bold"
          >
            <span class="icon mx-3">
              <i class="fa-solid fa-exclamation-triangle" />
            </span>
            <span class="text mx-1">No workflow repositories configured.</span>
          </div>
          <SelectContainer
            v-else
            v-model="selectedRepoModel"
            :options="wfRepositories"
            track-by="id"
            label="label"
            :allow-empty="false"
            :disabled="isLoadingRepositories"
            :loading="isLoadingRepositories"
            placeholder="Select a repository platform..."
          />
          <p
            v-if="isGalaxyEndpoint && hostMismatchError"
            class="help is-danger mt-2"
          >
            {{ hostMismatchError }}
          </p>
        </div>
      </div>
      <div class="tabs is-boxed is-small mt-2">
        <ul>
          <li
            :class="{ 'is-active': selectedTab === 'import' }"
            @click="changeTab('import')"
          >
            <a>
              <span class="icon is-small">
                <i class="fa-solid fa-globe" aria-hidden="true" />
              </span>
              <span>
                Import from
                {{ isGalaxyEndpoint ? selectedRepo.label : 'RO-Crate' }}
                URL
              </span>
            </a>
          </li>
          <li
            :class="{ 'is-active': selectedTab === 'search' }"
            @click="changeTab('search')"
          >
            <a>
              <span class="icon is-small">
                <i class="fa-solid fa-search" aria-hidden="true" />
              </span>
              <span v-if="selectedRepo">Search {{ selectedRepo.value }}</span>
            </a>
          </li>
        </ul>
      </div>
      <div class="has-borders">
        <div v-if="selectedTab === 'import'" class="p-3">
          <div class="columns">
            <div class="column is-four-fifths-fullhd">
              <div class="is-flex is-align-items-baseline mb-2">
                <span class="icon has-text-info py-4">
                  <i class="fa-solid fa-info-circle" />
                </span>
                <div class="mb-2">
                  <p v-if="!isGalaxyEndpoint" class="mb-1">
                    For example:
                    <code class="has-text-primary">
                      {{ exampleUrl }}
                    </code>
                  </p>
                  <p class="mb-1">
                    <template v-if="isGalaxyEndpoint">
                      Please provide a valid
                      {{ selectedRepo?.label }} workflow URL.
                    </template>
                    <template v-else>
                      Please provide a valid RO-Crate URL
                      <b>including the version.</b>
                    </template>
                  </p>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-body">
                  <div class="field">
                    <div class="field has-addons mb-0">
                      <div class="control is-expanded">
                        <input
                          :value="inputUrl"
                          class="input"
                          :class="{ 'is-danger': !isValidUrl }"
                          type="text"
                          :placeholder="
                            isGalaxyEndpoint
                              ? `Enter a ${selectedRepo?.label} workflow URL...`
                              : 'Enter RO-Crate URL...'
                          "
                          @input="inputUrl = $event.target.value.trim()"
                          @change="processUrl($event.target.value.trim())"
                          @keyup.enter="
                            inputUrl &&
                            isValidUrl &&
                            !isImporting &&
                            handleImportFromUrl()
                          "
                        />
                      </div>
                      <div class="control">
                        <button
                          class="button is-primary"
                          :class="{ 'is-loading': isImporting }"
                          :disabled="
                            !inputUrl ||
                            !isValidUrl ||
                            isImporting ||
                            !!hostMismatchError
                          "
                          @click="handleImportFromUrl"
                        >
                          <span class="icon">
                            <i class="fa-solid fa-download" />
                          </span>
                          <span>Import</span>
                        </button>
                      </div>
                    </div>
                    <p v-if="inputUrlError" class="help is-danger">
                      {{ inputUrlError }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="selectedTab === 'search'" class="panel-block">
          <div class="columns">
            <div class="column is-four-fifths-fullhd">
              <div class="is-flex is-align-items-baseline mb-2">
                <span class="icon has-text-info py-4">
                  <i class="fa-solid fa-info-circle" />
                </span>
                <div class="mb-2">
                  <template v-if="isGalaxyEndpoint">
                    The search targets
                    <code class="has-text-primary">name</code>
                    and
                    <code class="has-text-primary">description</code>
                    of workflows and
                    <b>ignores workflows that are not importable.</b>
                  </template>
                  <template v-else>
                    The search targets the
                    <code class="has-text-primary">name</code>
                    of each workflow. You can refine your search by using
                    metadata modifiers, such as
                    <code class="has-text-primary">description:workflow1</code>
                    or
                    <code class="has-text-primary">author:john.doe</code>
                    .
                  </template>
                </div>
              </div>
              <div class="field">
                <div class="field-body">
                  <div class="field has-addons">
                    <div class="control is-expanded has-icons-left">
                      <input
                        v-model.trim="searchQuery"
                        class="input"
                        type="text"
                        :placeholder="`Search ${
                          (selectedRepo && selectedRepo.value) || ''
                        }...`"
                        @change="!isSearching && searchWorkflows()"
                        @keyup.enter="!isSearching && searchWorkflows()"
                      />
                      <span class="icon is-small is-left">
                        <i class="fa-solid fa-search" />
                      </span>
                    </div>
                    <div class="control">
                      <button
                        class="button is-primary"
                        :class="{ 'is-loading': isSearching }"
                        :disabled="!searchQuery || isSearching"
                        @click="searchWorkflows"
                      >
                        <span class="icon">
                          <i class="fa-solid fa-arrow-right" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Results Section -->
      <div v-if="showSearchResults" class="panel-block">
        <div v-if="searchResults.length > 0" class="workflow-list">
          <div
            v-for="wf in searchResults"
            :key="wf.id"
            class="workflow-item box mb-4"
          >
            <div class="columns is-vcentered">
              <div
                class="column"
                @click.stop="wf.showDescription = !wf.showDescription"
              >
                <h4 class="title is-5">
                  <span>
                    {{ wf.name }}
                  </span>
                  <a
                    class="tag is-info is-light ml-2"
                    :href="wf.url"
                    target="_blank"
                    rel="noopener"
                    :title="wf.url"
                  >
                    ID: {{ wf.id }}
                  </a>
                </h4>
                <span
                  v-if="wf.sortedVersions.length > 0"
                  class="field is-grouped is-grouped-multiline"
                >
                  <div
                    v-for="version in wf.showAllVersions
                      ? wf.sortedVersions
                      : wf.sortedVersions.slice(0, SHOW_VERSION_COUNT)"
                    :key="version.id"
                    class="control"
                  >
                    <a
                      class="tags has-addons"
                      @click.stop="wf.selectedVersion = version"
                    >
                      <span
                        class="tag is-primary is-light is-hoverable highlight-border"
                      >
                        <input
                          type="radio"
                          style="cursor: pointer"
                          :name="`version-${wf.id}`"
                          :value="version.id"
                          :checked="wf.selectedVersion?.id === version.id"
                          @change="wf.selectedVersion = version"
                        />
                      </span>
                      <span
                        class="tag is-primary is-hoverable highlight-border"
                        :class="{
                          'is-light': wf.selectedVersion?.id !== version.id,
                        }"
                      >
                        {{ version.name }}
                      </span>
                    </a>
                  </div>
                  <a
                    v-if="wf.sortedVersions.length > 3 && !wf.showAllVersions"
                    class="tag button is-ghost"
                    @click.stop="wf.showAllVersions = true"
                  >
                    +{{ wf.sortedVersions.length - SHOW_VERSION_COUNT }}
                    more
                  </a>
                </span>
                <span v-else>No versions available</span>

                <!-- eslint-disable vue/no-v-html -->
                <div
                  v-if="wf.description"
                  class="subtitle is-6 description-preview"
                  :class="{ 'show-full': wf.showDescription }"
                  v-html="formatDescription(wf.description, wf.showDescription)"
                ></div>
                <!-- eslint-enable -->
                <div v-else class="subtitle is-6 has-text-grey">
                  No description available
                </div>
              </div>
              <div class="column is-narrow">
                <button
                  v-tooltip="
                    isImporting && selectedWorkflow?.id === wf.id
                      ? 'Importing...'
                      : !wf.selectedVersion
                        ? 'No workflow version selected'
                        : 'Import: ' + wf.name + ' ' + wf.selectedVersion?.name
                  "
                  class="button is-primary mt-auto"
                  :class="{
                    'is-loading': selectedWorkflow?.id === wf.id && isImporting,
                  }"
                  :disabled="isImporting || !wf.selectedVersion"
                  @click.stop="handleImportWorkflow(wf, wf.selectedVersion)"
                >
                  <span class="icon">
                    <i class="fa-solid fa-file-import" />
                  </span>
                  <span>Import {{ wf.selectedVersion?.name }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="
            !isLoading && searchResults.length === 0 && lastSearchQuery
          "
          class="panel-block p-0"
        >
          No workflows found matching your search
          <code class="has-text-primary ml-2 px-1">
            {{ lastSearchQuery }}
          </code>
          .
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// TODO(#1702-followup): migrate this file to <script lang="ts">. Left as JS in
// the #1702 bug-fix PR because a full conversion surfaces ~37 pre-existing
// implicit-any errors across untyped legacy code (search results, event
// handlers, notification callbacks) that are out of scope for the bug fix.
// The sibling view WorkflowRunImportGalaxyView.vue was migrated in the same PR.
import { useVuelidate } from '@vuelidate/core'
import { helpers, required, url } from '@vuelidate/validators'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { get, post } from '@/api'
import { useAppConfig } from '@/composables/useAppConfig'
import { usePageShell } from '@/composables/usePageShell'
import { useStore } from '@/store'
import { formatAndTruncateText } from '@/utils'

import PageTopBar from '@/components/PageTopBar.vue'
import SelectContainer from '@/components/SelectContainer.vue'

const SHOW_VERSION_COUNT = 3
const EXAMPLE_WORKFLOW_ID = '123'

const WH_HUB_URL_REGEX = /^.*\/workflows\/\d+(\/){0,1}.*$/
const RO_CRATE_VERSION_URL_REGEX =
  /^.*workflowhub.*\/workflows\/\d+\/ro_crate.*[?&]version=\d+/

export default {
  name: 'WorkflowImportROCrateView',
  components: {
    PageTopBar,
    SelectContainer,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const selectedTab = ref('import')
    // Initialize all reactive refs at the top
    const qPlatform = computed(() => route.query.platform || 'workflowhub')
    const workflowId = ref(route.query.workflow_id)
    // URL form
    const inputUrl = ref('')
    const isImporting = ref(false)
    const selectedWorkflow = ref(null)
    // Search form
    const searchQuery = ref('')
    const versionSortOrder = ref('desc') // 'desc' for newest first, 'asc' for oldest first
    // Selected workflow
    const workflowName = ref('')
    const isLoading = ref(false)

    usePageShell({ title: 'Import workflow' })

    const {
      isLoading: isLoadingRepositories,
      config,
      publicWorkflowRepositories: wfRepositories,
    } = useAppConfig()

    const exampleUrl = computed(() => {
      const repo = wfRepositories.value.find((e) =>
        e.value.includes('workflowhub'),
      )
      return repo?.url ? processROCrateUrl(repo.url, '1') : null
    })

    const qPlatformEndpoint = computed(() =>
      wfRepositories.value.find((e) => e.value.includes(qPlatform.value)),
    )
    const selectedRepoId = ref('')
    watchEffect(() => {
      selectedRepoId.value =
        wfRepositories.value.find((r) => r.value.includes(qPlatform.value))
          ?.id || ''
    })

    watch(selectedRepoId, (newId) => {
      if (!newId) return
      if (selectedTab.value === 'search') searchWorkflows()
      // Also trigger search if we have a pending searchQuery (e.g., from workflow version import)
      else if (searchQuery.value && workflowId.value) searchWorkflows()
    })
    const selectedRepo = computed(() =>
      wfRepositories.value.find((repo) => repo.value === selectedRepoId.value),
    )
    const selectedRepoModel = computed({
      get: () =>
        wfRepositories.value.find((r) => r.id === selectedRepoId.value) || null,
      set: (val) => {
        selectedRepoId.value = val?.id || ''
      },
    })
    const isWorkflowHub = computed(() => selectedRepo.value?.isWorkflowHub)
    const isGalaxyEndpoint = computed(() => selectedRepo.value?.isGalaxy)

    // Pre-flight host match: when the selected repo is a Galaxy server, the
    // pasted URL must belong to that same server. This catches the common case
    // of "picked Galaxy EMBL but pasted a usegalaxy.eu URL" before we hit the
    // server (which would otherwise fail with an opaque Galaxy 403).
    const hostMismatchError = computed(() => {
      if (!isGalaxyEndpoint.value) return null
      const repoHost = selectedRepo.value?.urlObject?.hostname
      const url = normalizedUrlValue.value
      if (!repoHost || !url) return null
      let inputHost
      try {
        inputHost = new URL(url).hostname
      } catch {
        return null
      }
      if (inputHost === repoHost) return null
      return `URL host "${inputHost}" doesn't match the selected server "${repoHost}". Change the server dropdown, or paste a URL from ${repoHost}.`
    })

    const urlValue = computed(() => (inputUrl.value || '').trim())

    const hasVersionQueryParamValue = () => {
      if (!normalizedUrlValue.value) return true
      try {
        const url = new URL(normalizedUrlValue.value)
        const version = url.searchParams.get('version')
        return version !== null && /^\d+$/.test(version)
      } catch {
        return false
      }
    }

    const isSupportedRepository = computed(() => {
      let normalized = normalizedUrlValue.value
      if (!normalized) return true
      normalized = normalized.toLowerCase()
      try {
        const url = new URL(normalized)
        return wfRepositories.value.some(
          ({ urlObject }) => url.hostname === urlObject.hostname,
        )
      } catch {
        return false
      }
    })
    const isWorkflowHubWorkflowUrl = computed(() => {
      if (!normalizedUrlValue.value) return true
      return WH_HUB_URL_REGEX.test(normalizedUrlValue.value)
    })

    const isWorkflowHubRoCrateUrl = computed(() => {
      if (!normalizedUrlValue.value) return true
      return RO_CRATE_VERSION_URL_REGEX.test(normalizedUrlValue.value)
    })

    const isFixableUrl = computed(
      () =>
        isSupportedRepository.value &&
        isWorkflowHubWorkflowUrl.value &&
        !isWorkflowHubRoCrateUrl.value,
    )

    const repositoryValidator = helpers.withMessage(
      'URL must be from one of the supported registries',
      () => isSupportedRepository.value,
    )
    const whHubUrlValidator = helpers.withMessage(
      'Not a valid Workflow Hub URL',
      () => isWorkflowHubWorkflowUrl.value,
    )
    const roCrateVersionUrlValidator = helpers.withMessage(
      'Not a valid RO-Crate URL',
      () => isWorkflowHubRoCrateUrl.value || isFixableUrl.value,
    )
    const versionQueryParamValidator = helpers.withMessage(
      'Version was not specified',
      (value) => hasVersionQueryParamValue(value),
    )
    const workflowHubValidations = computed(() => {
      let v = {}
      if (isWorkflowHub.value) {
        v = {
          whHubUrlValidator,
          roCrateVersionUrlValidator,
          versionQueryParamValidator,
        }
      }
      return v
    })
    const validationRules = computed(() => ({
      inputUrl: {
        required,
        url,
        repositoryValidator,
        ...workflowHubValidations.value,
      },
    }))

    const v$ = useVuelidate(validationRules, { inputUrl }, { $autoDirty: true })
    const inputValidationErrors = computed(
      () => v$.value.inputUrl.$errors || [],
    )
    const inputUrlError = computed(() => {
      const firstError = inputValidationErrors.value[0]
      return firstError ? firstError.$message : null
    })
    const isValidUrl = computed(() => inputValidationErrors.value.length === 0)

    function processROCrateUrl(_url = '', version = '') {
      let roCrateUrl
      try {
        const url = new URL(_url)
        // Extract the first two parts of pathname (/workflows/id) before adding ro_crate
        const pathParts = url.pathname.split('/').filter((v) => v)
        url.pathname = [
          'workflows',
          pathParts[1] || EXAMPLE_WORKFLOW_ID,
          'ro_crate',
        ].join('/')
        // Add version parameter if not present
        if (!url.searchParams.has('version')) {
          url.searchParams.set('version', version)
        }
        roCrateUrl = url.toString()
      } catch {
        roCrateUrl = _url
      }
      return roCrateUrl
    }

    function processUrl(url) {
      let processedUrl = url?.trim()

      if (isFixableUrl.value) {
        processedUrl = processROCrateUrl(processedUrl)
      }
      inputUrl.value = processedUrl
      return processedUrl
    }
    const isSearching = ref(false)
    const lastSearchQuery = ref('')
    const rawSearchResults = ref([])
    const searchResults = ref([])

    const showSearchResults = computed(
      () =>
        selectedTab.value === 'search' &&
        !isSearching.value &&
        lastSearchQuery.value,
    )

    const normalizedUrlValue = computed(() => urlValue.value?.trim())

    async function searchWorkflows() {
      if (!searchQuery.value) return
      // Guard against missing endpoint (config failed to load)
      if (!selectedRepo.value?.id) {
        store.commit('notify', {
          type: 'error',
          message:
            'No repository selected. Please select a repository platform.',
        })
        return
      }
      isSearching.value = true
      rawSearchResults.value = []
      lastSearchQuery.value = ''

      try {
        lastSearchQuery.value = searchQuery.value
        // Always use query_trs endpoint for searching, regardless of selected endpoint
        const response = await get('query_trs', {
          params: {
            endpoint: selectedRepo.value.id,
            query: searchQuery.value,
          },
        })
        const { status, data } = response
        if (status === 200) rawSearchResults.value = data
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Failed to search workflows'
        store.commit('notify', { type: 'error', message: message })
      } finally {
        isSearching.value = false
      }
    }

    watch(
      rawSearchResults,
      (newResults) => {
        searchResults.value = newResults.map((workflow) => {
          let sortedVersions = workflow.versions?.slice() || []
          if (versionSortOrder.value === 'desc') sortedVersions.reverse()
          // const selectedVersion = sortedVersions[0] || null
          return {
            ...workflow,
            sortedVersions,
            selectedVersion: null,
            showDescription: false,
            showAllVersions: false,
          }
        })
      },
      { immediate: true },
    )

    // Common import handler that processes the response and handles errors
    async function importWorkflow(
      importPromise,
      workflowDisplayName = 'Workflow',
    ) {
      isImporting.value = true

      try {
        const response = await importPromise

        const importedWorkflowId = response.data.results?.id?.value
        const importedWorkflowName =
          response.data.results?.name?.value?.name || workflowDisplayName
        const msg = {
          type: 'success',
          message: 'Workflow successfully imported.',
          action: (notification) => {
            notification.$router.push({
              path: `/${importedWorkflowId}`,
            })
          },
          actionText: importedWorkflowName,
          duration: 100000,
        }
        store.commit('notify', msg)

        if (response.data?.id) {
          router.push({
            name: 'workflowDetail',
            params: { id: response.data.id },
          })
        }
        return response
      } catch (error) {
        console.error('Import failed:', error)
        const errorData = error.response?.data
        if (errorData?.code === 'workflow_run_already_imported') {
          const existingRun = errorData.workflowrun
          store.commit('notify', {
            type: 'warning',
            message: `This workflow run has already been imported as "${existingRun.name}" (ID: ${existingRun.id})`,
            isDismissable: true,
            actions: [
              {
                text: 'View existing run',
                icon: 'fa-solid fa-external-link',
                onClick: () =>
                  router.push(`/workflows/workflowruns/${existingRun.id}`),
              },
            ],
          })
        } else {
          let errorCode = errorData?.code
          let errorMessage = errorData?.message || 'Failed to import workflow'

          if (errorCode === 'exists-provide-workflow-id-to-update') {
            errorMessage =
              'A workflow with the same name already exists, to update an existing workflow, please start from the Workflow detail page.'
          }

          store.commit('notify', {
            type: 'error',
            message: errorMessage,
          })
        }
      } finally {
        isImporting.value = false
      }
    }

    async function handleImportFromUrl() {
      const importUrl = inputUrl.value
      const importPromise = isGalaxyEndpoint.value
        ? importGalaxyWorkflow(importUrl)
        : importROCrateWorkflow(importUrl)
      await importWorkflow(importPromise)
    }

    function importGalaxyWorkflow(url) {
      return post('workflows/workflows/import_from_galaxy/', {
        data: { url, galaxy_server: selectedRepo.value?.url },
      })
    }

    function importROCrateWorkflow(url) {
      let importWfUrl = 'workflows/workflows/import-rocrate/'
      if (workflowId.value) {
        const searchParams = new URLSearchParams({
          workflow_id: workflowId.value,
        })
        importWfUrl += `?${searchParams.toString()}`
      }
      return post(importWfUrl, { data: { url } })
    }

    async function handleImportWorkflow(workflow, version) {
      selectedWorkflow.value = workflow
      try {
        const importUrl = isGalaxyEndpoint.value
          ? workflow.api_url
          : processROCrateUrl(version.url, version.id)
        const importPromise = isGalaxyEndpoint.value
          ? importGalaxyWorkflow(importUrl)
          : importROCrateWorkflow(importUrl)
        return importWorkflow(importPromise, workflow.name)
      } finally {
        selectedWorkflow.value = null
      }
    }
    async function fetchWorkflowDetails() {
      if (!workflowId.value) return
      isLoading.value = true
      const response = await get(`workflows/workflows/${workflowId.value}/`)
      if (response.status === 200) {
        const workflow = response.data.results
        workflowName.value = workflow.name.value.name
        searchQuery.value = `name:${workflow.name.value.name}`
        // Only search if repositories are loaded and selectedRepo is set
        if (selectedRepo.value?.id) {
          searchWorkflows()
        }
      }
    }

    function formatDescription(description, showFull = false) {
      return formatAndTruncateText(description, {
        maxLength: 200,
        showFull,
        DOMPurify,
        marked,
      })
    }

    function changeTab(tab) {
      selectedTab.value = tab
    }
    // Call fetchWorkflowDetails when component is mounted
    onMounted(async () => {
      await fetchWorkflowDetails()
    })

    return {
      SHOW_VERSION_COUNT,
      //
      selectedTab,
      changeTab,
      inputUrl,
      v$,
      inputUrlError,
      isValidUrl,
      processUrl,
      qPlatformEndpoint,
      //
      config,
      wfRepositories,
      isLoadingRepositories,
      exampleUrl,
      selectedRepo,
      selectedRepoId,
      selectedRepoModel,
      isGalaxyEndpoint,
      hostMismatchError,
      //
      showSearchResults,
      searchQuery,
      lastSearchQuery,
      searchWorkflows,
      isSearching,
      rawSearchResults,
      searchResults,
      selectedWorkflow,
      isImporting,
      // isImporting,
      isLoading,
      workflowId,
      workflowName,
      handleImportFromUrl,
      handleImportWorkflow,
      formatDescription,
      versionSortOrder,
    }
  },
}
</script>

<style lang="scss" scoped>
.tabs {
  margin-bottom: 0;
  li.is-active a {
    font-weight: bolder;
  }
}

.repository-platform-field {
  max-width: 24rem;
}

div.has-borders {
  border: 1px solid var(--color-border-default);
  border-top: 0;
  border-radius: 0 0 4px 4px;
  margin-bottom: 1rem;
}
.workflow-list {
  width: 100%;
}

.workflow-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    background-color: var(--bg-default);
  }

  &.is-selected {
    border-color: var(--brand);
  }
}

.tag.highlight-border {
  border-top: 1px solid var(--brand);
  border-bottom: 1px solid var(--brand);
  &:first-child {
    border-left: 1px solid var(--brand);
    border-right: 1px solid var(--brand);
  }
  &:last-child {
    border-right: 1px solid var(--brand);
  }
}

.workflow-item {
  margin-bottom: 1rem;

  .description-preview {
    max-height: 4.5em;
    overflow: hidden;
    position: relative;
    margin-bottom: 0.5rem;
    font-size: 0.8rem;

    // reset headers look normal, override in particular the style font-size: 100% which does the opposite of normal
    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      font-weight: bold;
    }
    :deep(h1) {
      font-size: 1.2rem;
    }
    :deep(h2) {
      font-size: 1.1rem;
    }
    :deep(h3) {
      font-size: 1rem;
    }
    :deep(h4) {
      font-size: 0.9rem;
    }
    :deep(h5) {
      font-size: 0.8rem;
    }
    :deep(h6) {
      font-size: 0.7rem;
    }

    &.show-full {
      max-height: none;

      &::after {
        display: none;
      }
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 1.5em;
      background: linear-gradient(transparent, white);
      pointer-events: none;
    }

    :deep(p) {
      margin-bottom: 0.5em;
    }

    :deep(ul),
    :deep(ol) {
      margin-left: 1.5em;
    }

    :deep(a) {
      color: #485fc7;
      text-decoration: underline;
    }
  }
}
</style>
